import classNames from "classnames";
import { useEffect, useState } from "react";
import { getOrganizationChart } from "../../http/chapterApi";
import {
  FunctionaryV2,
  Location,
  OrganizationChart,
} from "../../types/apiTypes";
import { LastViewedObject } from "../../types/types";
import Page from "../Page";
import useFetchPage from "../hooks/useFetchPage";
import styles from "./OrganizationChart.module.css";

interface Props {
  setLastViewed: (value: LastViewedObject) => void;
}

const Organigram = ({ setLastViewed }: Props) => {
  const page = useFetchPage(setLastViewed);
  const [organization, setOrganization] = useState<OrganizationChart>();

  useEffect(() => {
    (async () => setOrganization(await getOrganizationChart()))();
  }, []);

  return (
    page && (
      <Page title={page.frontmatter.title} description={page.content}>
        {organization && (
          <div className={styles.organigram}>
            {organization.president && (
              <div className={styles.section}>
                <div className={classNames(styles.row, styles.rowThree)}>
                  <div className={styles.center}>
                    <p className={styles.header}>Präsident/in</p>
                    <ul className={styles.row}>
                      <li className={styles.item}>
                        {organization.president.first_name}{" "}
                        {organization.president.last_name}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <div className={styles.section}>
              <p className={styles.header}>Landesausschuss</p>
              <ul className={classNames(styles.row, styles.rowThree)}>
                {organization.country_committee.map((item) => (
                  <li className={styles.item} key={item.id}>
                    {item.first_name} {item.last_name}
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.section}>
              <p className={styles.header}>Bezirk</p>
              <dl className={classNames(styles.row, styles.rowFour)}>
                {groupByLocation(organization.district_committee).map(
                  (district) =>
                    district.items.map((item) => {
                      if (!item.district) return null;
                      return (
                        <div className={styles.items} key={item.id}>
                          <dt className={classNames(styles.item, styles.dark)}>
                            {item.district.name}
                          </dt>
                          <dd className={styles.item}>
                            {item.first_name} {item.last_name}
                          </dd>
                        </div>
                      );
                    }),
                )}
              </dl>
            </div>
            <div className={styles.section}>
              <p className={styles.header}>Gebiet</p>
              <div className={classNames(styles.row, styles.rowFour)}>
                {groupByLocation(organization.area_committee).map((area) => (
                  <div key={area.district.id}>
                    {area.items.map((item) => {
                      if (!item.area) return null;
                      return (
                        <div className={styles.items} key={item.id}>
                          <dt className={classNames(styles.item, styles.dark)}>
                            {item.area.name}
                          </dt>
                          <dd className={styles.item}>
                            {item.first_name} {item.last_name}
                          </dd>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.section}>
              <div className={classNames(styles.row, styles.rowThree)}>
                <p className={classNames(styles.header, styles.center)}>
                  {organization.chapters} Ortsgruppen
                </p>
              </div>
              <div className={styles.row}>
                <p className={styles.item}>
                  ORTSOBMANN/-FRAU, ORTSAUSSCHUSS, ORTSVERSAMMLUNG
                </p>
              </div>
            </div>
            <div className={styles.section}>
              <div className={classNames(styles.row, styles.rowThree)}>
                <p className={classNames(styles.header, styles.center)}>
                  {organization.members} Mitglieder
                </p>
              </div>
            </div>
          </div>
        )}
      </Page>
    )
  );
};

export default Organigram;

interface GroupedLocation {
  district: Location;
  items: FunctionaryV2[];
}

const groupByLocation = (data: FunctionaryV2[]) => {
  const groupedData = data.reduce<Record<string, GroupedLocation>>(
    (acc, curr) => {
      if (curr.district === undefined) return acc;

      const key = curr.district.id;
      if (!acc[key]) {
        acc[key] = { district: curr.district, items: [] };
      }
      acc[key].items.push(curr);
      return acc;
    },
    {},
  );

  const ids = [7296, 6001, 7883];
  const sortedData = Object.values(groupedData).map((data) => {
    const sortedItems = data.items.sort((a) => (ids.includes(a.id) ? -1 : 1));

    return { ...data, items: sortedItems };
  });

  return Object.values(sortedData).sort((a, b) =>
    a.district.name < b.district.name ? -1 : 1,
  );
};
